import type { AxiosResponse } from 'axios'

import type {
    InternalOrderNumberConfigurationTypesResponseSchema,
    InternalOrderResponseSchema,
    InternalOrdersAddCommentPayloadSchema,
    InternalOrdersAddCommentResponseSchema,
    InternalOrdersCopyProductAttachmentsResultItemSchema,
    InternalOrdersCreateOrderPayloadSchema,
    InternalOrdersListResponseSchema,
    InternalOrderStatusesResponseSchema,
    InternalOrdersUpdateOrderPayloadSchema
} from '@/api/schemas/orders/internal'
import type {
    InternalOrderCreateProduct,
    InternalOrderCreateProductTemplate,
    InternalOrderCreateState,
    InternalOrderDetails,
    InternalOrderFilters,
    InternalOrderStatus,
    NumberConfigurationTypeItem
} from '@/types/domain/orders/internal'
import { internalOrdersService } from '@/views/Orders/InternalOrders/services/internalOrdersService'

import { buildOrderCreateStateFromOrder } from '@/views/Orders/InternalOrders/helpers/copyOrderHelpers'

import type { InternalOrdersState } from './state'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createInternalOrdersActions(state: InternalOrdersState) {
    const fetchOrders = async (queryString = ''): Promise<AxiosResponse<InternalOrdersListResponseSchema>> => {
        try {
            state.ordersLoading.value = true
            const response = await internalOrdersService.fetchOrders(queryString)
            state.orders.value = response.data.results || []
            return response
        } catch (error) {
            throw error
        } finally {
            state.ordersLoading.value = false
        }
    }

    const fetchOrder = async (orderId: number | string): Promise<AxiosResponse<InternalOrderResponseSchema>> => {
        try {
            state.ordersLoading.value = true
            const response = await internalOrdersService.fetchOrder(orderId)
            const data = response.data ?? ({} as InternalOrderDetails)
            state.order.value = {
                ...data,
                netPrice: data.netPrice != null ? Number(data.netPrice) : 0,
                valuation: data.valuation != null ? Number(data.valuation) : 0
            }
            return response
        } catch (error) {
            throw error
        } finally {
            state.ordersLoading.value = false
        }
    }

    const fetchOrderStatuses = async (): Promise<AxiosResponse<InternalOrderStatusesResponseSchema>> => {
        try {
            const response = await internalOrdersService.fetchOrderStatuses()
            const data = response.data as { results?: InternalOrderStatus[] } | InternalOrderStatus[] | undefined
            state.orderStatusList.value = Array.isArray(data) ? data : (data?.results ?? [])
            return response
        } catch (error) {
            throw error
        }
    }

    const fetchNumberConfigurationTypes = async (): Promise<
        AxiosResponse<InternalOrderNumberConfigurationTypesResponseSchema>
    > => {
        try {
            const response = await internalOrdersService.fetchNumberConfigurationTypes()
            const data = response.data as { results?: NumberConfigurationTypeItem[] } | NumberConfigurationTypeItem[] | undefined
            state.numberConfigurationTypes.value = Array.isArray(data) ? data : (data?.results ?? [])
            return response
        } catch (error) {
            throw error
        }
    }

    const createOrder = async (
        orderData: InternalOrdersCreateOrderPayloadSchema
    ): Promise<AxiosResponse<InternalOrderResponseSchema>> => {
        try {
            return await internalOrdersService.createOrder(orderData)
        } catch (error) {
            throw error
        }
    }

    const uploadDeliveryAttachment = async (formData: FormData): Promise<AxiosResponse<unknown>> => {
        try {
            return await internalOrdersService.uploadDeliveryAttachment(formData)
        } catch (error) {
            throw error
        }
    }

    const updateOrder = async (
        orderData: InternalOrdersUpdateOrderPayloadSchema
    ): Promise<AxiosResponse<InternalOrderResponseSchema>> => {
        try {
            state.ordersLoading.value = true
            return await internalOrdersService.updateOrder(orderData)
        } catch (error) {
            throw error
        } finally {
            state.ordersLoading.value = false
        }
    }

    const deleteOrder = async (orderId: number | string): Promise<AxiosResponse<unknown>> => {
        try {
            return await internalOrdersService.deleteOrder(orderId)
        } catch (error) {
            throw error
        }
    }

    const createJob = async (payload: Record<string, unknown>): Promise<AxiosResponse<{ id: number }>> => {
        try {
            return await internalOrdersService.createJob(payload)
        } catch (error) {
            throw error
        }
    }

    const addComment = async (
        commentData: InternalOrdersAddCommentPayloadSchema
    ): Promise<AxiosResponse<InternalOrdersAddCommentResponseSchema>> => {
        try {
            return await internalOrdersService.addComment(commentData)
        } catch (error) {
            throw error
        }
    }

    const setFilters = (filters: Partial<InternalOrderFilters>): void => {
        state.filters.value = { ...state.filters.value, ...filters }
    }

    const resetFilters = (): void => {
        state.filters.value = state.createInitialFilters()
    }

    const setOrderCreateTemporarySave = (payload: {
        order?: Partial<InternalOrderCreateState['order']>
        deliveryFile?: File | null
        adviceNoteDraft?: InternalOrderCreateState['adviceNoteDraft']
    }): void => {
        const current = state.orderCreate.value
        if (payload.order && current.order) {
            Object.assign(current.order, payload.order)
        }
        if (payload.deliveryFile !== undefined) {
            current.deliveryFile = payload.deliveryFile
        }
        if (payload.adviceNoteDraft !== undefined) {
            current.adviceNoteDraft = payload.adviceNoteDraft
        }
    }

    const addOrderCreateProduct = (payload: {
        product: InternalOrderCreateProduct
        productTemplate: InternalOrderCreateProductTemplate
    }): void => {
        const { product, productTemplate } = payload
        const current = state.orderCreate.value
        const newOrderProducts = [...current.order.orderProducts, product]
        const newProductTemplates = [...current.productTemplates, productTemplate]
        state.orderCreate.value = {
            ...current,
            order: { ...current.order, orderProducts: newOrderProducts },
            productTemplates: newProductTemplates
        }
    }

    const updateOrderCreateProduct = (payload: {
        index: number
        product: InternalOrderCreateProduct
        productTemplate: InternalOrderCreateProductTemplate
    }): void => {
        const { index, product, productTemplate } = payload
        const current = state.orderCreate.value
        const orderProducts = [...current.order.orderProducts]
        const productTemplates = [...current.productTemplates]
        if (index >= 0 && index < orderProducts.length) {
            orderProducts[index] = product
            productTemplates[index] = productTemplate
        }
        state.orderCreate.value = {
            ...current,
            order: { ...current.order, orderProducts },
            productTemplates
        }
    }

    const removeOrderCreateProduct = (index: number): void => {
        const current = state.orderCreate.value
        const orderProducts = current.order.orderProducts.filter((_, i) => i !== index)
        const productTemplates = current.productTemplates.filter((_, i) => i !== index)
        state.orderCreate.value = {
            ...current,
            order: { ...current.order, orderProducts },
            productTemplates
        }
    }

    const clearOrderCreate = (): void => {
        state.orderCreate.value = {
            order: state.createEmptyOrderCreateOrder(),
            productTemplates: [],
            deliveryFile: null,
            adviceNoteDraft: null
        }
    }

    const populateOrderCreateFromOrder = async (
        orderId: number,
        options: { copyAttachments: boolean; sourceOrder?: InternalOrderDetails }
    ): Promise<void> => {
        const [sourceOrder] = await Promise.all([
            options.sourceOrder
                ? Promise.resolve(options.sourceOrder)
                : internalOrdersService.fetchOrder(orderId).then((response) => response.data),
            state.numberConfigurationTypes.value.length === 0 ? fetchNumberConfigurationTypes() : Promise.resolve()
        ])
        let copiedAttachments: InternalOrdersCopyProductAttachmentsResultItemSchema[] = []

        if (options.copyAttachments) {
            const attachmentsResponse = await internalOrdersService.copyOrderProductAttachments({
                sourceOrderId: orderId
            })
            copiedAttachments = attachmentsResponse.data?.results ?? []
        }

        state.orderCreate.value = buildOrderCreateStateFromOrder(sourceOrder, {
            copyAttachments: options.copyAttachments,
            copiedAttachments,
            numberConfigurationTypes: state.numberConfigurationTypes.value
        })
    }

    return {
        fetchOrders,
        fetchOrder,
        fetchOrderStatuses,
        fetchNumberConfigurationTypes,
        createOrder,
        uploadDeliveryAttachment,
        updateOrder,
        deleteOrder,
        createJob,
        addComment,
        setFilters,
        resetFilters,
        setOrderCreateTemporarySave,
        addOrderCreateProduct,
        updateOrderCreateProduct,
        removeOrderCreateProduct,
        clearOrderCreate,
        populateOrderCreateFromOrder
    }
}
